import { Link } from "react-router-dom";
import { Check, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import { product } from "../content/site";
import { Reveal, Eyebrow } from "../components/Reveal";
import { BottleVisual } from "../components/BottleVisual";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Product() {
  const sections = [
    {
      id: "benefits",
      title: "Benefits",
      content: (
        <div>
          <p className="font-medium text-stone-900">{product.benefits.intro}</p>
          <p className="mt-2 text-sm leading-relaxed text-stone-600">{product.benefits.body}</p>
          <p className="mt-5 text-sm font-medium text-stone-900">{product.benefits.listTitle}</p>
          <ul className="mt-3 space-y-2.5">
            {product.benefits.items.map((b) => (
              <li key={b} className="flex items-start gap-3 text-sm text-stone-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-900" strokeWidth={1.5} />
                {b}
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      id: "ingredients",
      title: "Ingredients",
      content: (
        <div className="space-y-3 text-sm leading-relaxed text-stone-600">
          <p>{product.ingredients.main}</p>
          <p className="font-medium text-stone-900">{product.ingredients.none}</p>
          <p>{product.ingredients.perCapsule}</p>
        </div>
      ),
    },
    {
      id: "directions",
      title: "Directions for Use",
      content: (
        <div className="space-y-3 text-sm leading-relaxed text-stone-600">
          <p className="font-medium text-stone-900">{product.directions.dose}</p>
          <p>{product.directions.serving}</p>
          <ul className="mt-2 space-y-2.5 border-t border-stone-900/10 pt-4">
            {product.directions.warnings.map((w) => (
              <li key={w} className="flex items-start gap-3">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-emerald-900/60" />
                {w}
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      id: "nutrition",
      title: "Nutritional Information",
      content: (
        <div>
          <table data-testid="nutrition-table" className="w-full text-sm">
            <tbody>
              {product.nutrition.rows.map((r) => (
                <tr key={r.label} className="border-b border-stone-900/10">
                  <td className="py-3 pr-4 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                    {r.label}
                  </td>
                  <td className="py-3 text-stone-900">
                    {r.value}
                    {r.nrv && <span className="ml-3 font-mono text-xs text-emerald-900">{r.nrv}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-4 text-xs leading-relaxed text-stone-500">{product.nutrition.note}</p>
        </div>
      ),
    },
    {
      id: "claims",
      title: "Approved Health Claims",
      content: (
        <div>
          <p className="text-sm font-medium text-stone-900">Magnesium contributes to:</p>
          <ul className="mt-3 space-y-2.5">
            {product.claims.map((c) => (
              <li key={c} className="flex items-start gap-3 text-sm text-stone-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-900" strokeWidth={1.5} />
                {c}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm leading-relaxed text-stone-600">{product.claimsExtra}</p>
        </div>
      ),
    },
    {
      id: "product-faqs",
      title: "Product FAQs",
      content: (
        <ul className="space-y-5">
          {product.faqs.map((f) => (
            <li key={f.q}>
              <p className="font-medium text-stone-900">{f.q}</p>
              <p className="mt-1 text-sm leading-relaxed text-stone-600">{f.a}</p>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <div data-testid="product-page">
      <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-8 lg:px-16">
        <nav className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500" aria-label="Breadcrumb">
          <Link to="/" className="transition-colors duration-300 hover:text-stone-900">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/shop" className="transition-colors duration-300 hover:text-stone-900">Shop</Link>
          <span className="mx-2">/</span>
          <span className="text-stone-900">{product.name}</span>
        </nav>
      </div>

      <section className="mx-auto grid max-w-7xl gap-14 px-4 py-12 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-16 lg:py-16">
        {/* Gallery */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <Reveal>
            <div className="relative flex justify-center border border-stone-900/10 bg-[#F3EFE6] p-6 sm:p-8">
              <BottleVisual className="relative w-64 sm:w-80" />
              <div className="absolute inset-x-5 bottom-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                <span>{product.bottle}</span>
                <span data-testid="image-preview-note" className="text-emerald-900/80">
                  Preview image — not final packaging
                </span>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
              <span className="border border-stone-900/15 px-3 py-2">{product.capsules} capsules</span>
              <span className="border border-stone-900/15 px-3 py-2">90-day supply</span>
              <span className="border border-stone-900/15 px-3 py-2">{product.edition}</span>
            </div>
          </Reveal>
        </div>

        {/* Details */}
        <div>
          <Reveal>
            <Eyebrow>EL Well — {product.edition}</Eyebrow>
            <h1 className="mt-5 font-serif text-5xl font-light leading-[1.05] text-stone-900 sm:text-6xl">
              {product.name}
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-stone-600">{product.tagline}</p>

            <div className="mt-8 flex items-baseline gap-4">
              <p data-testid="product-price" className="font-serif text-4xl font-light text-stone-900">
                £{product.price.toFixed(2)}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                {product.supplyNote}
              </p>
            </div>

            <ul className="mt-8 space-y-3 border-t border-stone-900/10 pt-8">
              {product.pageHighlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-sm text-stone-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-900" strokeWidth={1.5} />
                  {h}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                type="button"
                data-testid="buy-now-button"
                disabled
                className="cursor-not-allowed bg-stone-900/40 px-8 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-stone-50/80"
              >
                Coming Soon
              </button>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                Launching soon — purchases open shortly
              </p>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4 border-t border-stone-900/10 pt-6">
              {[
                [Truck, "UK dispatch"],
                [RotateCcw, "14-day returns"],
                [ShieldCheck, "Launching soon"],
              ].map(([Icon, label]) => (
                <div key={label} className="flex items-center gap-2.5">
                  <Icon className="h-4 w-4 shrink-0 text-emerald-900" strokeWidth={1.5} />
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-stone-600">{label}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-12" data-testid="product-accordions">
              {sections.map((s) => (
                <AccordionItem key={s.id} value={s.id} className="border-stone-900/15" data-testid={`accordion-${s.id}`}>
                  <AccordionTrigger className="py-6 text-left font-serif text-xl font-light text-stone-900 hover:no-underline [&>svg]:text-emerald-900">
                    {s.title}
                  </AccordionTrigger>
                  <AccordionContent className="pb-8">{s.content}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* WHY EL WELL */}
      <section className="border-t border-stone-900/10" data-testid="product-why-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-24">
          <Reveal>
            <Eyebrow>Why EL Well?</Eyebrow>
            <p className="mt-6 max-w-2xl font-serif text-3xl font-light leading-snug text-stone-900 sm:text-4xl">
              People deserve supplements they can trust.
            </p>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-stone-600">
              That's why we carefully select premium ingredients and maintain complete transparency in every
              formulation.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Quality band */}
      <section className="bg-[#1E3A2D] text-stone-100" data-testid="product-quality-band">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
          <Reveal>
            <p className="font-mono text-[11px] font-medium uppercase tracking-[0.3em] text-emerald-100/70">
              Quality & Transparency
            </p>
            <h2 className="mt-5 max-w-2xl font-serif text-4xl font-light leading-tight text-stone-50 sm:text-5xl">
              What is on the label is what is in the bottle.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-10 sm:grid-cols-3">
            {product.qualityPoints.map((q, i) => (
              <Reveal key={q} delay={i * 0.08}>
                <div className="border-t border-stone-100/20 pt-6">
                  <p className="font-serif text-2xl font-light text-stone-100/50">0{i + 1}</p>
                  <p className="mt-3 text-sm leading-relaxed text-stone-200">{q}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/why-el-well"
              data-testid="product-quality-link"
              className="mt-12 inline-block border border-stone-100/40 px-8 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-100 transition-colors duration-300 hover:bg-stone-50 hover:text-stone-900"
            >
              Our Quality Promise
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
